import { Button, Spinner } from "components";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { FormField } from "pages/GenericOrder/ui-form/form-field";
import { CurrencyInput } from "components/Input/Inputs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "@/components/ui/form";
import { useMutation, useQuery } from "react-query";
// Same signature, answered by the new backend. See legacy-order-api.ts.
import { getCalculations } from "service/moneeflo/legacy-order-api";
import { ChevronDown, ChevronUp } from "lucide-react";
import { ChargeRow } from "pages/GenericOrder/steps/calculation-panel";
import { cancelForexOrder, getProductList } from "../apis";
import { z } from "zod";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from "@radix-ui/react-alert-dialog";
import {
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import CancelOrderDialog from "./CancelOrderDialog";
import { subtract } from "lodash";

const refundReasons = [
  {
    value: "Incomplete / incorrect documentation",
    label: "Incomplete / incorrect documentation",
  },
  {
    value: "Mismatch in traveler details",
    label: "Mismatch in traveler details",
  },
  {
    value: "Customer not willing to pay TCS",
    label: "Customer not willing to pay TCS",
  },
  { value: "Wrong product selected", label: "Wrong product selected" },
  {
    label: "Change of travel plan / trip cancelled",
    value: "Change of travel plan / trip cancelled",
  },
  {
    label: "Did not like the quality of currency notes",
    value: "Did not like the quality of currency notes",
  },
];

const formSchema = z.object({
  reason: z
    .string()
    .min(1, { message: "Please select a reason for cancellation" }),
  quantity: z
    .number({ required_error: "Please enter cancellation amount" })
    .positive({ message: "Amount must be greater than 0" }),
});

// Mock API call to get tax rates
const fetchTaxRates = async () => {
  await new Promise((resolve) => setTimeout(resolve, 300));
  return {
    cgst: 0.09,
    sgst: 0.09,
  };
};

export default function CancelForexOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [productTotal, setProductTotal] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);
  const [showAdditionalCharges, setShowAdditionalCharges] = useState(false);
  const [redirectToRefund, setRedirectToRefund] = useState(false);
  const [productBreakdown, setProductBreakdown] = useState<
    Array<{
      label: string;
      currency: string;
      amount: number;
      value: number;
      rate: number;
    }>
  >([]);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reason: "",
      quantity: undefined,
    },
  });

  const { data: taxRates } = useQuery({
    queryKey: ["taxRates"],
    queryFn: fetchTaxRates,
    initialData: { cgst: 0.09, sgst: 0.09 },
  });

  const { data: productList, isLoading: productLoading } = useQuery({
    queryKey: ["productList", id],
    queryFn: () => getProductList(Number(id)),
  });

  // Process product breakdown whenever productList changes
  useEffect(() => {
    if (!productList?.product) return;

    const groupedProducts: Record<
      string,
      {
        label: string;
        currency: string;
        amount: number;
        value: number;
        rate: number;
      }
    > = {};

    productList.product.forEach((product: any) => {
      const currencyCode = product?.country_currency_want || "UNKNOWN";
      const amount = product?.quantity || 0;
      const rate =
        parseFloat(product?.rate_and_margins?.[0]?.ibr_rate?.toString()) || 0;
      const value = product?.inr_amount || 0;
      const key = currencyCode;

      if (!groupedProducts[key]) {
        groupedProducts[key] = {
          label: key,
          currency: currencyCode,
          amount: 0,
          value: 0,
          rate: rate,
        };
      }

      groupedProducts[key].amount += amount;
      groupedProducts[key].value += value;
    });

    const productList_breakdown = Object.values(groupedProducts);
    setProductBreakdown(productList_breakdown);

    // Calculate subtotal from all products
    const subtotal = productList_breakdown.reduce(
      (sum, item) => sum + item.value,
      0
    );
    setProductTotal(subtotal);
  }, [productList, taxRates]);

  const productCount = productBreakdown.length;

  const { data: additionalCharges, isLoading: isLoadingAdditionalCharges } =
    useQuery({
      queryKey: ["additionalCharges", productTotal, productCount],
      queryFn: () => getCalculations({ amount: productTotal, type: "buy" }),
      enabled: productCount > 0 && productTotal > 0,
    });

  const { mutate, isLoading: loading } = useMutation({
    mutationFn: cancelForexOrder,
    onSuccess: (data, variables) => {
      toast.success("Cancel request submitted successfully");
      setIsDialogOpen(false);
      setPendingFormData(null);
      console.log("variables", variables);
      setTimeout(() => {
        if (variables.redirectToRefund) {
          navigate(`/refund/forex/${id}`, {
            state: {
              cancelReason: variables.reason,
              cancelAmount: variables.amount,
            },
          }); // ✅ properly closed
        } else {
          navigate(`/forex/orderdetails/${id}`);
        }
      }, 2000);
    },
    onError: () => {
      toast.error("Failed to submit cancel request");
      setIsDialogOpen(false);
      setPendingFormData(null);
    },
  });

  const handleFormSubmit = (data: any) => {
    setPendingFormData(data);
    setIsDialogOpen(true);
  };

  const handleConfirmCancellation = () => {
    if (pendingFormData) {
      mutate({
        reason: pendingFormData.reason,
        amount: pendingFormData.quantity,
        order_id: Number(id),
        redirectToRefund: false, // 🚀 pass the flag here
      });
    }
  };

  const handleSubmitAndInitiateRefund = async (e: React.MouseEvent) => {
    e.preventDefault();

    const values = form.getValues();
    if (!values.reason || !values.quantity) {
      toast.error("Please complete all fields before submitting.");
      return;
    }

    mutate({
      reason: values.reason,
      amount: values.quantity,
      order_id: Number(id),
      redirectToRefund: true,
    });
  };

  return (
    <>
      {loading && <Spinner />}
      <CancelOrderDialog
        isOpen={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        onConfirm={handleConfirmCancellation}
        isLoading={loading}
      />
      <div className="sm:p-1 flex font-inter items-start justify-between gap-5 overflow-hidden mx-auto w-full h-full">
        <section className="gap-5 bg-white-A700 w-full flex h-full flex-col p-3 rounded-lg">
          <div className="flex flex-col justify-between h-full gap-5 w-full sm:px-0 overflow-auto">
            <div className="flex px-10 relative flex-col gap-5 overflow-y-auto flex-grow">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleFormSubmit)}
                  className="space-y-6 max-w-md"
                >
                  <div className="space-y-6">
                    {/* Reason */}
                    <div className="flex gap-4">
                      {/* Timeline */}
                      <div className="flex flex-col items-center pt-1">
                        <div
                          className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 transition-colors ${
                            form.watch("reason")
                              ? "border-[#165FE3] bg-[#165FE3]"
                              : "border-gray-400 bg-white"
                          }`}
                        ></div>
                        <div
                          className={`w-px flex-1 border-l-2 border-dashed mt-2 transition-colors ${
                            form.watch("reason")
                              ? "border-[#165FE3]"
                              : "border-gray-300"
                          }`}
                        ></div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-2 pb-4">
                        <label className="text-sm font-medium text-gray-700">
                          Reason for cancellation
                        </label>
                        <FormField
                          form={form}
                          name="reason"
                          render={({ field }) => (
                            <>
                              <Select
                                onValueChange={field.onChange}
                                value={field.value}
                              >
                                <SelectTrigger className="w-full h-12 px-4 border-gray-300 rounded-lg">
                                  <SelectValue placeholder="Select a reason for cancellation" />
                                </SelectTrigger>
                                <SelectContent>
                                  {refundReasons.map((reason) => (
                                    <SelectItem
                                      key={reason.value}
                                      value={reason.value}
                                    >
                                      {reason.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {form.formState.errors.reason && (
                                <p className="text-sm text-red-600 mt-1">
                                  {form.formState.errors.reason.message}
                                </p>
                              )}
                            </>
                          )}
                        />
                      </div>
                    </div>

                    {/* Amount */}
                    <div className="flex gap-4">
                      {/* Timeline */}
                      <div className="flex flex-col items-center pt-1">
                        <div
                          className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 transition-colors ${
                            form.watch("quantity")
                              ? "border-[#165FE3] bg-[#165FE3]"
                              : "border-gray-400 bg-white"
                          }`}
                        ></div>
                        <div className="w-px flex-1 opacity-0"></div>
                      </div>

                      {/* Content */}
                      <div className="flex-1 space-y-2 pb-4">
                        <label className="text-sm font-medium text-gray-700">
                          Charges for cancellation
                        </label>
                        <FormField
                          form={form}
                          name="quantity"
                          render={({ field }) => (
                            <>
                              <CurrencyInput
                                field={{
                                  ...field,
                                  onChange: (e) => {
                                    const value = e.target.value;
                                    if (
                                      value === "" ||
                                      /^\d*\.?\d*$/.test(value)
                                    ) {
                                      const numValue =
                                        value === ""
                                          ? undefined
                                          : parseFloat(value);
                                      field.onChange(numValue);
                                    }
                                  },
                                }}
                                currencyType={"INR"}
                              />                              
                            </>
                          )}
                        />
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-3 pl-6">
                      <button
                        onClick={handleSubmitAndInitiateRefund}
                        type="button"
                        disabled={
                          !form.watch("reason") ||
                          !form.watch("quantity") ||
                          loading
                        }
                        className="px-6 py-2.5 text-sm font-medium rounded-lg text-blue-600 border border-blue-600 bg-white hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
                      >
                        Submit and Initiate Refund
                      </button>
                      <button
                        type="submit"
                        className="px-6 py-2.5 text-sm font-medium rounded-lg text-[#ffffff] bg-blue-600 hover:bg-blue-700 transition-colors"
                      >
                        Submit
                      </button>
                    </div>
                  </div>
                </form>
              </Form>
            </div>
          </div>
        </section>

        {/* Calculator section */}
        <section className="bg-white-A700 h-full rounded-lg w-1/3 p-3">
          <div className="w-full bg-white rounded-xl p-6 shadow-[0px_4px_4px_2px_rgba(239,241,251,1)]">
            <h2 className="text-2xl font-bold mb-6">Calculation</h2>
            <div className="flex flex-col gap-8">
              {/* Product breakdown */}
              <div className="flex flex-col gap-3">
                {productBreakdown?.map((item, index) => (
                  <div key={index}>
                    <div className="flex justify-between items-center">
                      <span className="font-inter font-medium text-sm leading-none tracking-[-0.04em] text-gray-800">
                        <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">
                          {item?.label}
                        </span>{" "}
                        {"  "}
                        {item?.amount} <span className="text-[#5777B2]">@</span>{" "}
                        <span className="text-[#8D8D8D] text-sm font-normal leading-[112%]">
                          INR
                        </span>{" "}
                        {item?.rate}
                      </span>
                      <div className="flex items-center">
                        <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded-md mr-2">
                          {item?.currency}
                        </span>
                        <span className="font-inter font-medium text-base leading-[16px] tracking-[-0.01em]">
                          {item?.value.toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </div>
                    </div>
                    {index !== productBreakdown.length - 1 && (
                      <div className="w-full h-px bg-gray-100" />
                    )}
                  </div>
                ))}
              </div>

              {/* Additional charges */}
              <div className="flex flex-col gap-5">
                <div
                  className="flex items-center justify-between h-[23px] rounded-[12px] pt-1 pr-3 pb-1 pl-3 bg-gray-50 font-medium cursor-pointer select-none"
                  onClick={() =>
                    setShowAdditionalCharges(!showAdditionalCharges)
                  }
                >
                  <span className="font-medium">Additional charges</span>
                  {showAdditionalCharges ? (
                    <ChevronUp size={20} />
                  ) : (
                    <ChevronDown size={20} />
                  )}
                </div>
                {showAdditionalCharges && (
                  <div className="space-y-0 mt-2">
                    {isLoadingAdditionalCharges ? (
                      <div className="text-center text-gray-400 py-4">
                        Loading charges...
                      </div>
                    ) : !additionalCharges ? (
                      <div className="text-center text-gray-400 py-4">
                        No additional charges available
                      </div>
                    ) : (
                      <>
                        <ChargeRow
                          label={`CGST @ ${(taxRates?.cgst || 0) * 100}%`}
                          amount={additionalCharges?.CGST}
                        />
                        <ChargeRow
                          label={`SGST @ ${(taxRates?.sgst || 0) * 100}%`}
                          amount={additionalCharges?.SGST}
                        />
                        <ChargeRow
                          label="Round off"
                          amount={additionalCharges?.roundOff}
                        />
                      </>
                    )}
                  </div>
                )}

                <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] ">
                  <span className="font-inter font-semibold text-base leading-none ">
                    Order total
                  </span>
                  <div className="flex items-center">
                    <span className="font-inter font-normal text-[#999999] text-[16px] leading-[1.12] tracking-[-0.01em]">
                      INR
                    </span>

                    <span className="font-inter font-medium text-base leading-[1.5] text-[#333333] ml-3">
                      {additionalCharges?.total_payable?.grand_total
                        ? additionalCharges.total_payable.grand_total.toLocaleString(
                            undefined,
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }
                          )
                        : "0.00"}
                    </span>
                  </div>
                </div>

                <div className="flex justify-between items-center px-[15px] py-[17px] ">
                  <span className="font-inter font-medium text-xs text-[#666666] leading-[100%] tracking-[-0.02em] not-italic ">
                    cancellation charge
                  </span>
                  <div className="flex justify-center items-center">
                    <span className="font-inter font-normal text-[#FF0000] text-[16px] leading-[1.12] tracking-[-0.01em]">
                      - INR
                    </span>

                    <span className="font-inter font-medium text-base leading-[1.5] text-[#FF0000] ml-3">
                      {form.watch("quantity") || 0}
                    </span>
                  </div>
                </div>
                {/* Grand Total */}
                <div className="flex justify-between items-center px-[15px] py-[17px] bg-[#f2f6ff] border-t-2 border-[#176bef] rounded-b-xl">
                  <span className="font-inter font-semibold text-base leading-none text-[#176bef]">
                    Total Refund Amount
                  </span>
                  <div className="flex items-center">
                    <span className="bg-[#176bef] text-white-A700 w-[29px] h-[19px] rounded-[8px] px-[6px] flex items-center justify-center">
                      <span className="font-inter font-normal text-[10px] leading-[1.12] tracking-[-0.01em]">
                        INR
                      </span>
                    </span>
                    <span className="font-inter font-medium text-base leading-[1.5] text-[#176bef] ml-3">
                      {subtract(
                        additionalCharges?.total_payable?.grand_total ?? 0,
                        form.watch("quantity") || 0
                      ).toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 h-full flex justify-end items-end">
            <div className="flex flex-col gap-3 p-2">
              <p className="font-normal leading-[19.2px] text-xs text-[#666666]">
                The above amount is an estimate and may change based on
                real-time fx rates and remitter's current FY transactions.
              </p>
              <h4 className="font-medium leading-[19.2px] text-xs text-[#232323]">
                Decimals are rounded off.
              </h4>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
